// src/data/worksheets/focus.js — performance-side pair (authored, not converted).
// Built off the Focus action plan (work in blocks / clear the desk / silence
// the pings) plus attention-control practice: cue words, a reset routine,
// distraction rules and a daily tracker. Neutral voice; adaptText handles the
// sport -> corporate wording where it matters.

export const FOCUS_CHECKLIST = {
    id: 'focus_checklist', topicId: 'focus', kind: 'checklist',
    title: 'Focus Checklist',
    intro: 'Attention is trainable. Tick these off as you build them in — it saves as you go.',
    sections: [
        { id: 'environment', title: 'Set Up Your Environment', prompts: [
            { id: 'friction', type: 'checklist', label: 'Remove Friction', items: [
                { id: 'f1', label: '{Clear the space you work or train in|Clear your workspace} — clutter quietly taxes attention.' },
                { id: 'f2', label: 'Put your phone out of reach or on Do Not Disturb during your key blocks.' },
                { id: 'f3', label: 'Decide the one thing that matters most today and write it down where you can see it.' }
            ] },
            { id: 'noise', type: 'checklist', label: 'Batch the Noise', items: [
                { id: 'n1', label: 'Check messages at set times instead of continuously.' },
                { id: 'n2', label: 'Turn off non-essential notifications on every device.' }
            ] }
        ] },
        { id: 'train', title: 'Train Attention Daily', prompts: [
            { id: 'blocks', type: 'checklist', label: 'Work in Blocks', items: [
                { id: 'b1', label: '50 minutes on one thing, then 10 minutes fully off screens.' },
                { id: 'b2', label: "Start with the hardest task while you're fresh." },
                { id: 'b3', label: 'One task, one tab — close the rest.' }
            ] },
            { id: 'reps', type: 'checklist', label: 'Mindfulness Reps', items: [
                { id: 'r1', label: 'Five minutes of breath-focused attention every day.', detail: 'Sit, breathe, count the exhales to ten, start again.' },
                { id: 'r2', label: 'When your mind wanders, notice it and return — that IS the rep.' }
            ] }
        ] },
        { id: 'performance', title: 'Focus Under Pressure', prompts: [
            { id: 'cues', type: 'checklist', label: 'Cue Words', items: [
                { id: 'c1', label: 'Pick one or two cue words for your key moments (e.g., "here", "next", "smooth").' },
                { id: 'c2', label: 'Use them in {training and everyday work|everyday work} so they hold up when it counts.' }
            ] },
            { id: 'reset', type: 'checklist', label: 'Reset Routine', items: [
                { id: 'rs1', label: 'Build a 10-second reset for after a mistake: one breath, cue word, posture.' },
                { id: 'rs2', label: "Practice it until it's automatic — the goal is to be back in the moment, not perfect." }
            ] },
            { id: 'prep', type: 'checklist', label: 'Pre-Performance Routine', items: [
                { id: 'p1', label: 'Write a 60-second routine you run before key moments (breathing, cue, first action).' },
                { id: 'p2', label: 'Run it every time, not just when it matters.' }
            ] }
        ] },
        { id: 'energy', title: 'Protect the Energy Behind Focus', prompts: [
            { id: 'sleep', type: 'checklist', label: 'Sleep and Fuel', items: [
                { id: 's1', label: 'Aim for 7–9 hours of sleep — attention is the first thing sleep loss takes.' },
                { id: 's2', label: 'No caffeine within 10 hours of bedtime.' },
                { id: 's3', label: "Eat before you're empty; hunger scatters concentration." }
            ] },
            { id: 'breaks', type: 'checklist', label: 'Real Breaks', items: [
                { id: 'bk1', label: 'On a break, move and look at something far away — no screens.' },
                { id: 'bk2', label: 'Take 60 seconds to breathe between tasks instead of rushing from one to the next.' }
            ] }
        ] },
        { id: 'distractions', title: 'Manage Distractions', prompts: [
            { id: 'leaks', type: 'checklist', label: 'Know Your Leaks', items: [
                { id: 'l1', label: 'Write down your top three distractions.' },
                { id: 'l2', label: 'For each one, set a single rule that blocks it.' }
            ] },
            { id: 'thoughts', type: 'checklist', label: 'Thought Traffic', items: [
                { id: 't1', label: 'When a worry shows up mid-task, park it on paper and come back to it later.' },
                { id: 't2', label: 'Notice result-thinking ("what if I mess up") and redirect to the next action.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'w1', label: 'Rate your focus for the week and note what helped.' },
                { id: 'w2', label: "Keep what worked, drop one thing that didn't." }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'Focus is a skill, not a mood. Small, repeated reps beat big intentions.'
};

export const FOCUS_WORKSHEET = {
    id: 'focus_worksheet', topicId: 'focus', kind: 'worksheet',
    title: 'Focus Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'level', type: 'scale', label: 'Rate your focus over the past week', help: 'How well have you been able to stay on what matters, when it matters?', min: 1, max: 10, minLabel: 'Scattered', maxLabel: 'Locked in' },
            { id: 'distractions', type: 'list', label: 'Where does your attention go?', help: 'Your top three distractions (e.g., phone, other people, worrying about results, tiredness).', count: 3 },
            { id: 'timing', type: 'text', label: 'When is your focus best, and when is it worst?', help: 'Time of day, setting, before or after what.', multiline: true },
            { id: 'cost', type: 'text', label: 'What does losing focus cost you?', help: 'In performance, in work, in how you feel afterwards.', multiline: true }
        ] },
        { id: 'understanding', title: 'Understanding Your Attention', prompts: [
            { id: 'situations', type: 'list', label: 'Situations where you lose focus', help: 'Be specific: the moment, not the whole day (e.g., after a mistake, the last 20 minutes, right after lunch).', count: 3 },
            { id: 'thoughts', type: 'list', label: 'Thoughts that pull you out of the moment', help: 'e.g., "what if I mess up", "I should be further along", "they\'re watching".', count: 3 },
            { id: 'best', type: 'text', label: 'Your best focused moment recently — what was different?', help: 'Sleep, preparation, environment, mindset. This is your template.', multiline: true }
        ] },
        { id: 'building', title: 'Building Focus', prompts: [
            { id: 'cues', type: 'list', label: 'Cue words', help: 'One or two short words that bring you back to the task (e.g., "here", "next", "smooth").', count: 2 },
            { id: 'routine', type: 'text', label: 'Your 60-second pre-performance routine', help: 'What you do right before a key moment: breathing, cue word, first action.', multiline: true },
            { id: 'reset', type: 'text', label: 'Your reset after a mistake', help: 'Ten seconds: one breath, your cue, your posture. Write it as you\'ll do it.', multiline: true },
            { id: 'rules', type: 'list', label: 'Distraction rules', help: 'One rule per distraction from your self-assessment (e.g., "phone in the bag until the block ends").', count: 3 }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'goal', type: 'text', label: "This week's focus goal", help: 'Specific and small — e.g., "two 50-minute blocks a day", "run my reset every time".' },
            { id: 'protect', type: 'text', label: 'The one task or skill you will protect time for' },
            { id: 'support', type: 'list', label: 'Who can help you hold the line', help: '{e.g., a training partner, a colleague, a coach.|e.g., a colleague, a mentor, a friend.}', count: 2 }
        ] },
        { id: 'track', title: 'Track It', description: 'A few seconds a day. The chart shows whether the blocks and the rules are moving your focus score.', prompts: [
            { id: 'tracker', type: 'table', label: 'Daily focus tracker', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'focus', label: 'Focus (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'blocks', label: 'Focus blocks done', kind: 'number', min: 0, max: 10 },
                { id: 'distraction', label: 'Biggest distraction', kind: 'text' },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'focus', label: 'Focus' }, { id: 'blocks', label: 'Blocks' }] } }
        ] }
    ],
    outro: 'Attention drifts — that is normal. The skill is noticing sooner and coming back faster.'
};

export const FOCUS_WORKSHEETS = [FOCUS_CHECKLIST, FOCUS_WORKSHEET];
